import {
  V2Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Button,
  VisuallyHidden,
  Form,
  FormField,
  FormSelect,
  FormInput,
  FormItem,
  FormLabel,
  FormMessage,
  FormDescription,
  V2Loader,
  Alert,
  AlertDescription,
  cn,
} from "web-design-system";
import {
  Client,
  PlansWithPlanType,
  SIPPlan,
  XSIPTransactionOrder,
} from "creso-core/types";
import {
  useMandates,
  useModifySipTransaction,
  useUpdateSipTransaction,
  useSchemes,
  useDraftTransactionOrders,
  useGetFolioNumbers,
  usePlanStartDate,
  useNextStepupInstallmentDate,
} from "creso-core/hooks";
import { getErrorMessage } from "@/utils/error";
import { useEffect, useState, useMemo, useRef } from "react";
import { useRouter } from "@tanstack/react-router";
import { useForm, useFieldArray, UseFormReturn } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  MandateStatus,
  TRANSACTION_ORDER_TYPE_LABELS,
  StepupFrequency,
} from "creso-core/constants";
import {
  convertToCurrency,
  formatAsCrore,
  getFirstName,
} from "creso-core/utils";
import { maskValue } from "@core/utils/common";
import { getFormattedAmount } from "@funds/utils";
import { format, parse, isValid, isBefore, startOfDay } from "date-fns";
import { AlertCircle, AlertTriangle } from "lucide-react";
import capitalize from "lodash/capitalize";
import { toast } from "sonner";
import {
  AmcLogo,
  OrderUpdates,
  SuccessLottieAnimation,
} from "web-core/components";
import {
  StepUpSipConfigComponent,
  StepUpSipValue,
} from "@transactions/components/sip-step-up-config";
import { PlanStartDate } from "web-core";
import { stepupFrequencySchema } from "creso-core/zod";

type Frequency = "DAILY" | "WEEKLY" | "MONTHLY";

type MandateItem = NonNullable<
  ReturnType<typeof useMandates>["mandates"]
>[number];

type Props = PlansWithPlanType & {
  client: Client;
  open: boolean;
  toggle: (show: boolean) => void;
};

// Type for a draft order carrying the pre-modification snapshot.
type XSIPOrderWithOldDetails = XSIPTransactionOrder;

const isValidDate = (d: string) => isValid(new Date(d));

const isNotInThePast = (d: string) =>
  !isBefore(new Date(d), startOfDay(new Date()));

// The form stores only what's needed to render fields + build the payload. UI
// helpers (matched scheme, min date, pending draft) are resolved per card.
const planFormSchema = z
  .object({
    id: z.number(),
    scheme_isin: z.string(),
    scheme_name: z.string(),
    amc_morningstar_code: z.string(),
    folio_id: z.number().nullable(),
    frequency: z.enum(["DAILY", "WEEKLY", "MONTHLY"]),
    first_order_today: z.boolean(),
    amount: z.string(),
    start_date: z.string(),
    next_installment_date: z.string(),
    mandate: z
      .object({
        id: z.number(),
        mandate_limit: z.number().nullable().optional(),
      })
      .passthrough()
      .nullable(),
    minimum_installment_amount: z.string(),
    // Id of an existing pending MODIFY draft order, if any.
    order_id: z.number().nullable(),
    is_editing_draft: z.boolean(),
    step_up_amount: z.number().nullable(),
    step_up_percentage: z.number().nullable(),
    step_up_frequency: stepupFrequencySchema,
  })
  .superRefine((plan, ctx) => {
    // Plans with a pending draft that the user hasn't chosen to edit are not
    // submitted, so skip their validation.
    if (plan.order_id != null && !plan.is_editing_draft) return;

    if (!plan.start_date) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Start date is required",
        path: ["start_date"],
      });
    } else if (!isValidDate(plan.start_date)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Start date is invalid",
        path: ["start_date"],
      });
    } else if (!isNotInThePast(plan.start_date)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Start date cannot be in the past",
        path: ["start_date"],
      });
    }

    if (!plan.mandate?.id) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Mandate is required",
        path: ["mandate"],
      });
    }

    if (!plan.amount) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Amount is required",
        path: ["amount"],
      });
    } else {
      const amount = parseFloat(plan.amount);
      const minAmount = parseFloat(plan.minimum_installment_amount || "0");
      if (amount < minAmount) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `Amount should be greater than or equal to ${convertToCurrency(minAmount.toString(), 2)}`,
          path: ["amount"],
        });
      }
      const limit = plan.mandate?.mandate_limit;
      if (limit && amount > limit) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: `Amount should be less than or equal to ${convertToCurrency(limit.toString(), 2)}`,
          path: ["amount"],
        });
      }
    }

    if (
      plan.step_up_frequency &&
      !plan.step_up_amount &&
      !plan.step_up_percentage
    ) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Required",
        path: ["step_up_amount"],
      });
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Required",
        path: ["step_up_percentage"],
      });
    }

    if (plan.step_up_amount && Number(plan.step_up_amount) <= 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Amount should be greater than 0",
        path: ["step_up_amount"],
      });
    }

    if (plan.step_up_percentage && Number(plan.step_up_percentage) <= 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Percentage should be greater than 0",
        path: ["step_up_percentage"],
      });
    }
  });

const modifySipFormSchema = z.object({
  plans: z.array(planFormSchema),
});

type ModifySipFormValues = z.infer<typeof modifySipFormSchema>;
type PlanFormValue = ModifySipFormValues["plans"][number];

const getStepUpPayload = (plan: PlanFormValue) => ({
  step_up_frequency: (plan.step_up_frequency
    ? Number(plan.step_up_frequency)
    : null) as StepupFrequency,
  step_up_amount: plan.step_up_amount ? Number(plan.step_up_amount) : null,
  step_up_percentage: plan.step_up_percentage
    ? Number(plan.step_up_percentage)
    : null,
});

// Read-only summary card for a pending draft modification.
function PendingDraftOrderCard({
  order,
  mandates,
  onEdit,
}: {
  order: XSIPOrderWithOldDetails;
  mandates?: MandateItem[];
  onEdit: () => void;
}) {
  const frequencyText = order.frequency
    ? `${capitalize(order.frequency)} on ${format(
        new Date(order.start_date as string),
        order.frequency === "WEEKLY" ? "EEE" : "do"
      )}`
    : "";

  return (
    <div className="border rounded-lg p-4 bg-card">
      <div className="flex justify-between items-start">
        <div className="flex flex-col gap-1">
          <span className="text-sm font-medium">
            Modify SIP
            {frequencyText ? (
              <span className="text-sm text-muted-foreground font-medium">
                {" "}
                · {frequencyText}
              </span>
            ) : (
              ""
            )}
          </span>
        </div>
        <span className="text-sm font-semibold">
          {convertToCurrency(order.amount)}
        </span>
      </div>
      <div className="flex items-center gap-2 mt-3">
        <AmcLogo amc_code={order.amc_morningstar_code} />
        <span className="text-sm font-medium line-clamp-2">
          {order.scheme_name}
        </span>
      </div>
      <OrderUpdates order={order} mandates={mandates} />
      <div className="flex justify-between items-center mt-3">
        <div className="flex items-center gap-2">
          <AlertCircle className="h-4 w-4" />
          <span className="text-sm">
            This SIP already has pending modifications
          </span>
        </div>
        <Button variant="outline" size="sm" onClick={onEdit}>
          Edit
        </Button>
      </div>
    </div>
  );
}

type PlanModifyCardProps = {
  form: UseFormReturn<ModifySipFormValues>;
  index: number;
  planCount: number;
  mandates?: MandateItem[];
  mandateOptions: Array<{ label: string; value: string }>;
  folioNumber?: string;
  draftOrders?: XSIPOrderWithOldDetails[];
  error?: string;
  onEditDraft: (index: number, order: XSIPOrderWithOldDetails) => void;
};

// Renders the editable form fields (or the pending-draft summary) for a single
// plan, owning the per-plan async data (matched scheme, min date, draft order).
function PlanModifyCard({
  form,
  index,
  planCount,
  mandates,
  mandateOptions,
  folioNumber,
  draftOrders,
  error,
  onEditDraft,
}: PlanModifyCardProps) {
  const isSinglePlan = planCount <= 1;
  const plan = form.watch(`plans.${index}`);

  const { schemes } = useSchemes<"sip">({
    type: "sip",
    search: plan.scheme_name,
    frequency: plan.frequency,
    enabled: !!plan.scheme_name,
  });

  const matchedScheme = useMemo(() => {
    if (!schemes?.length) return undefined;
    return (
      schemes.find(
        (s) =>
          s.scheme_name === plan.scheme_name &&
          (s.frequency === plan.frequency || !s.frequency)
      ) ??
      schemes.find((s) => s.scheme_name === plan.scheme_name) ??
      schemes[0]
    );
  }, [schemes, plan.scheme_name, plan.frequency]);

  // Feed the catalog min installment amount into the form so validation can
  // use it without the schema needing to fetch.
  useEffect(() => {
    if (matchedScheme?.minimum_installment_amount) {
      form.setValue(
        `plans.${index}.minimum_installment_amount`,
        matchedScheme.minimum_installment_amount,
        { shouldValidate: false }
      );
    }
  }, [matchedScheme?.minimum_installment_amount, form, index]);

  const pendingModifyOrder = useMemo(() => {
    return draftOrders?.find(
      (order) =>
        order.type === "XSIP" &&
        order.action_type === "MODIFY" &&
        order.old_plan_details?.id === plan.id
    );
  }, [draftOrders, plan.id]);

  // Keep the draft order id in the form state so submit/validation can react.
  useEffect(() => {
    if ((pendingModifyOrder?.id ?? null) !== plan.order_id) {
      form.setValue(`plans.${index}.order_id`, pendingModifyOrder?.id ?? null, {
        shouldValidate: false,
      });
    }
  }, [pendingModifyOrder?.id, plan.order_id, form, index]);

  const [minDate] = usePlanStartDate({
    planType: "XSIP",
    frequency: plan.frequency,
    firstOrderToday: false,
  });

  const nextInstallmentDate = useMemo(() => {
    try {
      return format(
        parse(plan.start_date, "yyyy-MM-dd", new Date()),
        "dd MMM yyyy"
      );
    } catch {
      return "";
    }
  }, [plan.start_date]);

  const isDateInNextMonth = useMemo(() => {
    if (!plan.start_date || !minDate) return false;
    try {
      const processedDate = parse(plan.start_date, "yyyy-MM-dd", new Date());
      processedDate.setHours(0, 0, 0, 0);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      const thisMonthDate = new Date(
        today.getFullYear(),
        today.getMonth(),
        processedDate.getDate()
      );
      return thisMonthDate > today && thisMonthDate < minDate;
    } catch {
      return false;
    }
  }, [plan.start_date, minDate]);

  const isBlocked = !!pendingModifyOrder && !plan.is_editing_draft;
  const shouldShowDateAlert =
    (!pendingModifyOrder || plan.is_editing_draft) && isDateInNextMonth;

  const { data: nextStepUpData } = useNextStepupInstallmentDate(
    {
      nextInstallmentDate: plan.start_date,
      stepUpFrequency: plan.step_up_frequency as StepupFrequency,
      frequency: plan.frequency,
    },
    {
      enabled: !isBlocked && !!plan.start_date && !!plan.step_up_frequency,
    }
  );

  const nextStepUpDate = useMemo(() => {
    if (!nextStepUpData?.next_step_up_date) return null;
    try {
      return format(
        new Date(nextStepUpData?.next_step_up_date),
        "dd MMMM yyyy"
      );
    } catch {
      return null;
    }
  }, [nextStepUpData]);

  return (
    <div className="pb-6 px-1 flex flex-col">
      {isBlocked && pendingModifyOrder ? (
        <div className="flex flex-col gap-3">
          <PendingDraftOrderCard
            order={pendingModifyOrder}
            mandates={mandates}
            onEdit={() => onEditDraft(index, pendingModifyOrder)}
          />
        </div>
      ) : (
        <>
          <div className="flex flex-row items-center gap-3">
            <AmcLogo size="sm" amc_code={plan.amc_morningstar_code} />
            <span className="text-sm font-semibold line-clamp-2 flex-1">
              {plan.scheme_name}
            </span>
            <span className="text-xs px-2 bg-slate-100 py-1 rounded font-semibold">
              {capitalize(plan.frequency)}
            </span>
            {folioNumber ? (
              <span className="text-xs font-semibold px-2 bg-slate-100 py-1 rounded">
                {folioNumber}
              </span>
            ) : null}
          </div>
          <div className="flex flex-col w-full items-start gap-4 mt-4">
            <div
              className={cn(
                "grid items-start gap-4 w-full",
                isSinglePlan ? "grid-cols-1" : "grid-cols-3"
              )}
            >
              <FormField
                control={form.control}
                className="col-span-1"
                name={`plans.${index}.start_date`}
                render={({ field }) => (
                  <FormItem className="w-full">
                    <FormLabel>
                      {plan.frequency === "WEEKLY" ? "Day" : "Date"}
                    </FormLabel>
                    <PlanStartDate
                      planType="XSIP"
                      frequency={plan.frequency}
                      firstOrderToday={false}
                      placeholder={
                        plan.frequency === "WEEKLY"
                          ? "Select start day"
                          : "Select start date"
                      }
                      onChange={field.onChange}
                      value={field.value}
                    />
                    {field.value && (
                      <FormDescription>
                        Next installment on {nextInstallmentDate}
                      </FormDescription>
                    )}
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                className="col-span-1"
                name={`plans.${index}.amount`}
                render={({ field }) => {
                  const minAmount =
                    matchedScheme?.minimum_installment_amount || "0";
                  return (
                    <FormInput
                      type="number"
                      label="Amount"
                      description={`Min ${getFormattedAmount(minAmount)}`}
                      {...field}
                    />
                  );
                }}
              />
              <FormField
                className="col-span-1"
                control={form.control}
                name={`plans.${index}.mandate`}
                render={({ field }) => (
                  <FormSelect
                    key={`mandate-select-${index}-${mandateOptions.length}`}
                    name={field.name}
                    onBlur={field.onBlur}
                    options={mandateOptions}
                    label="Choose Mandate"
                    align="end"
                    value={field.value?.id?.toString() ?? ""}
                    onChange={(value) => {
                      field.onChange(
                        mandates?.find((m) => String(m.id) === value) ?? null
                      );
                    }}
                  />
                )}
              />
            </div>
            {shouldShowDateAlert && (
              <Alert
                variant="default"
                className="bg-yellow-50 border-yellow-200 text-yellow-900 [&>svg]:text-yellow-600"
              >
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription className="text-yellow-900">
                  {`This month's installment will be skipped to meet minimum start date requirements.`}
                </AlertDescription>
              </Alert>
            )}
            <div className="rounded-lg bg-muted p-4 w-full">
              <StepUpSipConfigComponent
                value={{
                  amount: plan.step_up_amount,
                  percentage: plan.step_up_percentage,
                  frequency: plan.step_up_frequency,
                }}
                onChange={(value: StepUpSipValue) => {
                  form.setValue(`plans.${index}.step_up_amount`, value.amount, {
                    shouldDirty: true,
                  });
                  form.setValue(
                    `plans.${index}.step_up_percentage`,
                    value.percentage
                  );
                  form.setValue(
                    `plans.${index}.step_up_frequency`,
                    value.frequency
                  );
                }}
                errors={{
                  amount:
                    form.formState.errors.plans?.[index]?.step_up_amount
                      ?.message,
                  percentage:
                    form.formState.errors.plans?.[index]?.step_up_percentage
                      ?.message,
                }}
                nextStepUpDate={
                  plan.step_up_frequency && nextStepUpDate
                    ? nextStepUpDate
                    : undefined
                }
              />
            </div>
          </div>
        </>
      )}

      {error && <span className="text-xs text-destructive mt-3">{error}</span>}
    </div>
  );
}

export function ModifySIPDialog(props: Props) {
  const { plans, planType, client } = props;
  const investmentAccountGuid = client.investment_account ?? "";

  const { mandates } = useMandates(investmentAccountGuid);

  const { data: draftOrdersData, isLoading: isLoadingDraftOrders } =
    useDraftTransactionOrders(
      investmentAccountGuid,
      props.open && !!investmentAccountGuid
    );

  const draftOrders = useMemo(
    () => (draftOrdersData?.orders?.results ?? []) as XSIPOrderWithOldDetails[],
    [draftOrdersData]
  );

  const { data: folioNumbers } = useGetFolioNumbers({
    investment_account_guid: investmentAccountGuid,
  });

  const { mutateAsync: modifySipTransaction } = useModifySipTransaction(
    investmentAccountGuid
  );
  const { mutateAsync: updateSipTransaction } = useUpdateSipTransaction(
    investmentAccountGuid
  );

  const form = useForm<ModifySipFormValues>({
    resolver: zodResolver(modifySipFormSchema),
    defaultValues: { plans: [] },
  });

  const { fields, replace } = useFieldArray({
    control: form.control,
    name: "plans",
  });

  // Snapshot of the original values per plan id, used for change detection.
  const originalsRef = useRef<
    Record<
      number,
      {
        amount: string;
        mandate_id: number | null;
        start_date: string;
        step_up_amount: string | null;
        step_up_percentage: string | null;
        step_up_frequency: number | null;
      }
    >
  >({});
  const initializedRef = useRef(false);

  const [errors, setErrors] = useState<Record<number, string>>({});
  const [isSuccess, setIsSuccess] = useState(false);

  useEffect(() => {
    if (!props.open) {
      initializedRef.current = false;
      setIsSuccess(false);
      return;
    }
    if (initializedRef.current || !mandates) return;

    const sipPlans = plans as SIPPlan[];
    const initial: PlanFormValue[] = sipPlans.map((plan) => {
      const stepUpAmount =
        plan.step_up_amount != null ? Number(plan.step_up_amount) : null;
      const stepUpPercentage =
        plan.step_up_percentage != null
          ? Number(plan.step_up_percentage)
          : null;
      return {
        id: plan.id,
        scheme_isin: plan.scheme_isin,
        scheme_name: plan.scheme_name,
        amc_morningstar_code: plan.amc_morningstar_code,
        folio_id: plan.folio_id ?? null,
        frequency: plan.frequency as Frequency,
        first_order_today: plan.first_order_today ?? false,
        amount: plan.installment_amount,
        start_date: plan.start_date,
        next_installment_date: plan.installment_due_date as string,
        mandate: mandates.find((m) => Number(plan.mandate_id) === m.id) ?? null,
        minimum_installment_amount: "0",
        order_id: null,
        is_editing_draft: false,
        step_up_amount: stepUpAmount,
        step_up_percentage: stepUpPercentage,
        step_up_frequency: (plan.step_up_frequency ?? null) as StepupFrequency,
      };
    });

    originalsRef.current = Object.fromEntries(
      sipPlans.map((plan) => [
        plan.id,
        {
          amount: plan.installment_amount,
          mandate_id: Number(plan.mandate_id) || null,
          start_date: plan.start_date,
          step_up_amount:
            plan.step_up_amount != null ? String(plan.step_up_amount) : null,
          step_up_percentage:
            plan.step_up_percentage != null
              ? String(plan.step_up_percentage)
              : null,
          step_up_frequency: plan.step_up_frequency ?? null,
        },
      ])
    );

    replace(initial);
    setErrors({});
    initializedRef.current = true;
  }, [props.open, mandates, plans, replace]);

  const folioNumberById = useMemo(() => {
    const map: Record<number, string> = {};
    folioNumbers?.forEach((folio: { id: number; folio_number: string }) => {
      map[folio.id] = folio.folio_number;
    });
    return map;
  }, [folioNumbers]);

  const mandateOptions = useMemo(() => {
    return (
      mandates
        ?.filter((d) => d.status !== MandateStatus.REJECTED)
        ?.map((d) => ({
          label: d.bank_account_details
            ? `${d.bank_account_details?.bank_name} ${maskValue(d.bank_account_details?.account_number, 4)} • ${d.status} • ${formatAsCrore(d.mandate_limit?.toString() ?? "0", 0)}`
            : d.id?.toString(),
          value: d.id?.toString(),
        })) || []
    );
  }, [mandates]);

  const watchedPlans = form.watch("plans");

  const isPlanChanged = (plan: PlanFormValue) => {
    const original = originalsRef.current[plan.id];
    if (!original) return true;
    const amountChanged =
      !!plan.amount &&
      !!original.amount &&
      convertToCurrency(plan.amount) !== convertToCurrency(original.amount);
    const mandateChanged =
      (plan.mandate?.id ?? null) !== (original.mandate_id ?? null);
    const startDateChanged = (() => {
      if (!plan.start_date || !original.start_date) return false;
      try {
        return (
          new Date(plan.start_date).getDate() !==
          new Date(original.start_date).getDate()
        );
      } catch {
        return String(plan.start_date) !== String(original.start_date);
      }
    })();
    const stepUpChanged =
      plan.step_up_amount !== original.step_up_amount ||
      plan.step_up_percentage !== original.step_up_percentage ||
      plan.step_up_frequency !== original.step_up_frequency;
    return amountChanged || mandateChanged || startDateChanged || stepUpChanged;
  };

  const isPlanBlocked = (plan: PlanFormValue) =>
    plan.order_id != null && !plan.is_editing_draft;

  const submittablePlans = (watchedPlans ?? []).filter(
    (p) => !isPlanBlocked(p) && isPlanChanged(p)
  );

  const router = useRouter();

  const handleEditDraft = (index: number, order: XSIPOrderWithOldDetails) => {
    form.setValue(`plans.${index}.is_editing_draft`, true);
    if (order.amount != null) {
      form.setValue(`plans.${index}.amount`, order.amount);
    }
    if (order.start_date != null) {
      form.setValue(`plans.${index}.start_date`, order.start_date as string);
    }
    const mandate = mandates?.find((m) => m.id === order.mandate_id) ?? null;
    form.setValue(`plans.${index}.mandate`, mandate);
    form.setValue(`plans.${index}.step_up_frequency`, order.step_up_frequency);
    form.setValue(`plans.${index}.step_up_amount`, order.step_up_amount);
    form.setValue(
      `plans.${index}.step_up_percentage`,
      order.step_up_percentage
    );
  };

  const handleModify = async (values: ModifySipFormValues) => {
    const toSubmit = values.plans.filter(
      (p) => !isPlanBlocked(p) && isPlanChanged(p)
    );
    if (toSubmit.length === 0) return;

    const results = await Promise.allSettled(
      toSubmit.map((plan) => {
        const payload = {
          investment_account_guid: investmentAccountGuid,
          scheme_isin: plan.scheme_isin,
          folio_id: plan.folio_id ? Number(plan.folio_id) : null,
          amount: plan.amount,
          frequency: plan.frequency,
          first_order_today: false,
          mandate_id: plan.mandate?.id as number,
          start_date: plan.start_date,
          ...getStepUpPayload(plan),
        };

        if (plan.order_id != null && plan.is_editing_draft) {
          return updateSipTransaction({ id: plan.order_id, ...payload });
        }
        return modifySipTransaction({ id: plan.id, ...payload });
      })
    );

    const failedIds = new Set<number>();
    const nextErrors: Record<number, string> = {};
    results.forEach((result, idx) => {
      if (result.status === "rejected") {
        const plan = toSubmit[idx];
        failedIds.add(plan.id);
        nextErrors[plan.id] = getErrorMessage(
          result.reason,
          "Failed to modify SIP"
        );
      }
    });

    if (failedIds.size === 0) {
      setIsSuccess(true);
      return;
    }

    // Drop the plans that succeeded, keep the rest (failed + untouched) so the
    // user can review and retry.
    const succeededIds = new Set(
      toSubmit.map((p) => p.id).filter((id) => !failedIds.has(id))
    );
    replace(values.plans.filter((p) => !succeededIds.has(p.id)));
    setErrors(nextErrors);
    toast.error(
      `Failed to modify ${failedIds.size} of ${toSubmit.length} ${TRANSACTION_ORDER_TYPE_LABELS[planType]}${toSubmit.length > 1 ? "s" : ""}`
    );
  };

  const isLoading = isLoadingDraftOrders || !mandates;

  const submitLabel =
    submittablePlans.length > 0 &&
    submittablePlans.every((p) => p.order_id != null && p.is_editing_draft)
      ? "Update"
      : "Modify";

  const openCart = () => {
    router.navigate({
      to: ".",
      search: (prev) => ({
        ...prev,
        modal: "transaction",
        action: "view",
        client_id: client.id,
      }),
    });
    props.toggle(false);
  };

  return (
    <V2Dialog open={props.open} onOpenChange={props.toggle}>
      <DialogContent
        className={cn("p-6 gap-4 max-w-none w-auto flex flex-col min-h-128")}
      >
        {isSuccess ? (
          <div className="w-80 flex flex-col flex-1">
            <VisuallyHidden>
              <DialogTitle>Added to cart</DialogTitle>
            </VisuallyHidden>
            <div className="flex flex-col flex-1 items-center gap-2 py-4">
              <SuccessLottieAnimation loop={false} height={150} width={150} />
              <span className="text-lg font-semibold">Added to cart</span>
              <span className="text-sm text-muted-foreground text-center">
                {getFirstName(client.name)} needs to authorise SIP cancellation
                from their device.
              </span>
            </div>
            <DialogFooter className="mt-auto">
              <Button variant="outline" onClick={() => props.toggle(false)}>
                Close
              </Button>
              <Button onClick={openCart}>Open Cart</Button>
            </DialogFooter>
          </div>
        ) : (
          <div
            className={cn(
              "flex flex-col gap-4 flex-1",
              fields.length > 1 ? "w-200" : "w-180"
            )}
          >
            <DialogHeader>
              <VisuallyHidden>
                <DialogTitle className="text-lg">
                  Modify {TRANSACTION_ORDER_TYPE_LABELS[planType]}
                </DialogTitle>
              </VisuallyHidden>
              <div className="flex flex-col gap-4">
                <div className="text-lg font-semibold">
                  Modify {TRANSACTION_ORDER_TYPE_LABELS[planType]}
                </div>
                <div className="bg-border h-px w-full" />
              </div>
            </DialogHeader>
            <V2Loader
              isLoading={isLoading}
              className="min-h-50 flex-1 flex flex-col"
            >
              <Form {...form}>
                <form
                  onSubmit={form.handleSubmit(handleModify)}
                  className="flex flex-col divide-y divide-border gap-6 max-h-[60vh] overflow-y-auto pr-1"
                >
                  {fields.map((field, index) => {
                    const planId = watchedPlans?.[index]?.id ?? field.id;
                    return (
                      <PlanModifyCard
                        key={field.id}
                        form={form}
                        index={index}
                        planCount={fields.length}
                        mandates={mandates}
                        mandateOptions={mandateOptions}
                        folioNumber={
                          watchedPlans?.[index]?.folio_id != null
                            ? folioNumberById[
                                Number(watchedPlans[index].folio_id)
                              ]
                            : undefined
                        }
                        draftOrders={draftOrders}
                        error={errors[Number(planId)]}
                        onEditDraft={handleEditDraft}
                      />
                    );
                  })}
                </form>
              </Form>
            </V2Loader>
            <DialogFooter className="mt-auto">
              <Button variant="outline" onClick={() => props.toggle(false)}>
                Cancel
              </Button>
              <Button
                onClick={form.handleSubmit(handleModify)}
                disabled={isLoading || submittablePlans.length === 0}
              >
                {submitLabel}
              </Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </V2Dialog>
  );
}
